import React from 'react';

import {NucleotideType} from 'biojs/BioConstants';
import {MAX_PRIMER_NAME_LENGTH} from 'constants/PrimerConstants';
import {EnzymeAttributes} from 'molbio/enzymes/backbone/Enzymes';
import getTestProps from 'test/lib/cypress/getTestProps';
import makeDeprecatedJadeStyleTemplate from 'util/makeDeprecatedJadeStyleTemplate';

import EditPrimerBasesWarning from './EditPrimerBasesWarning';

export interface EditPrimerProps {
  assemblyPrimerHelp: string;
  enzymes: Array<EnzymeAttributes>;
  isGoldenGate: boolean;
  showEnzymes: boolean;
  primer1Exists: boolean;
  primer2Exists: boolean;
}

function EditPrimer({
  assemblyPrimerHelp,
  enzymes,
  isGoldenGate,
  showEnzymes,
  primer1Exists,
}: EditPrimerProps): JSX.Element {
  return (
    <form className="buffer-content-container form-horizontal">
      <fieldset>
        <legend>Design</legend>
        <div className="row js-use-existing-primer-group use-existing-primer hidden">
          <div className="col col-md-9 col-md-offset-3 js-use-existing-primer invisible" />
        </div>
        <div className="form-group nucleotide-type-group hidden">
          <label className="col-md-3 control-label">Nucleotide Type</label>
          <div className="col col-md-9 nucleotide-type">
            <label className="radio-inline">
              <input
                type="radio"
                name="primer-nucleotide-type"
                className="js-nucleotide-type"
                defaultChecked={true}
                value={NucleotideType.DNA}
              />
              DNA
            </label>
            <label className="radio-inline">
              <input
                type="radio"
                name="primer-nucleotide-type"
                className="js-nucleotide-type"
                value={NucleotideType.RNA}
              />
              RNA
            </label>
          </div>
        </div>
        <div className="form-group">
          <label className="col-md-3 control-label">Strand</label>
          <div className="col col-md-9">
            <label className="radio-inline">
              <input
                type="radio"
                name="primer-strand"
                className="js-strand"
                defaultChecked={true}
                value="forward"
              />
              Forward
            </label>
            <label className="radio-inline">
              <input type="radio" name="primer-strand" className="js-strand" value="reverse" />
              Reverse
            </label>
          </div>
        </div>
        <div className="form-group">
          <label className="col-md-3 control-label">Bases</label>
          <div className="col col-md-9">
            {primer1Exists && <EditPrimerBasesWarning />}
            <div className="sequence-primer-bases">
              <span className="prime five-prime">5'</span>
              <div className="primer-text-outer mono">
                <div
                  contentEditable={true}
                  className="primer-text"
                  {...getTestProps({component: 'EditPrimer', element: 'primerText'})}
                />
              </div>
              <span className="prime three-prime">3'</span>
            </div>
            <span
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{__html: assemblyPrimerHelp}}
            />
          </div>
        </div>
        <div className="form-group too-short-msg-group">
          <div className="col col-md-9 col-md-offset-3 too-short-msg">
            <p className="error-msg">Primer must be at least 6 bp.</p>
          </div>
        </div>
        <div className="row js-found-existing-primer-group hidden">
          <div className="col col-md-9 col-md-offset-3 js-found-existing-primer invisible" />
        </div>
        <div className="form-group primer-position-group">
          <label className="col-md-3 control-label">3' Location</label>
          <div className="col col-md-3 primer-position">
            <input
              type="number"
              name="primer-start-position"
              placeholder="3' end"
              className="form-control js-primer-position"
            />
          </div>
        </div>
        <div className="form-group bad-location-msg-group hidden">
          <div className="col col-md-9 col-md-offset-3 bad-location-msg invisible">
            <p className="error-msg">
              Binding location should be within the sequence. Using bind location of{' '}
              <span className="location-num" /> instead.
            </p>
          </div>
        </div>
        <div className="form-group overhang-group">
          <label className="col-md-3 control-label">Overhang</label>
          <div className="col col-md-3 overhang">
            <input
              type="number"
              name="primer-overhang-size"
              placeholder="0 bp"
              min="0"
              step="1"
              className="form-control js-overhang-size"
            />
          </div>
        </div>
        <div className="form-group binding-group hidden">
          <label className="col-md-3 control-label">
            Binding
            {isGoldenGate && (
              <>
                &nbsp;
                <a
                  title="Length of sequence-defined region"
                  className="glyphicon glyphicon-question-sign u-no-text-decoration"
                />
              </>
            )}
          </label>
          <div className="col col-md-3 binding invisible">
            <input
              type="number"
              name="primer-binding"
              placeholder="0 bp"
              min="0"
              step="1"
              className="form-control js-binding"
              {...getTestProps({component: 'EditPrimer', element: 'primerBindingInput'})}
            />
          </div>
        </div>
        <div className="form-group homology-group hidden">
          <label className="col-md-3 control-label">Overhang</label>
          <div className="col col-md-3 homology invisible">
            <input
              type="number"
              name="primer-homology"
              placeholder="0 bp"
              min="0"
              step="1"
              className="form-control js-homology"
              {...getTestProps({component: 'EditPrimer', element: 'OverhangInput'})}
            />
          </div>
        </div>
        <div className="form-group pre-recognition-site-group hidden">
          <label className="col-md-3 control-label">
            Pre-recog. Site&nbsp;
            <a
              title="5' flanking bases before recognition site"
              className="glyphicon glyphicon-question-sign u-no-text-decoration"
            />
          </label>
          <div className="col col-md-6 pre-recognition-site invisible">
            <input
              type="text"
              name="primer-pre-recognition-site"
              placeholder="Enter the pre-recognition site bases"
              className="form-control js-pre-recognition-site"
            />
          </div>
        </div>
        <div className="form-group pre-recognition-site-invalid-bases-msg-group hidden">
          <div className="col col-md-9 col-md-offset-3 pre-recognition-site-invalid-bases-msg invisible">
            <p className="error-msg">
              The pre-recognition site cannot contain the same bases as the recognition site.
            </p>
          </div>
        </div>
        <div className="form-group post-recognition-site-group hidden">
          <label className="col-md-3 control-label">
            Post-recog. Site&nbsp;
            <a
              title="Base(s) after recognition site"
              className="glyphicon glyphicon-question-sign u-no-text-decoration"
            />
          </label>
          <div className="col col-md-6 post-recognition-site invisible">
            <input
              type="text"
              name="primer-post-recognition-site"
              placeholder="Enter the post-recognition site bases"
              className="form-control js-post-recognition-site"
            />
          </div>
        </div>
        <div className="form-group post-recognition-site-invalid-length-msg-group hidden">
          <div className="col col-md-9 col-md-offset-3 post-recognition-site-invalid-length-msg invisible">
            <p className="error-msg">
              The post-recognition site must be exactly <span className="valid-length" /> bp.
            </p>
          </div>
        </div>
        <div className="form-group overhang-too-small-msg-group hidden">
          <div className="col col-md-9 col-md-offset-3 overhang-too-small-msg invisible">
            <p className="error-msg">
              The number of primer bases lying off of the end of the sequence cannot be higher than the
              overhang size. Using overhang size of <span className="overhang-num" /> instead.
            </p>
          </div>
        </div>
        <div className="form-group overhang-too-large-msg-group hidden">
          <div className="col col-md-9 col-md-offset-3 overhang-too-large-msg invisible">
            <p className="error-msg">
              Overhang size cannot be longer than primer size. Using overhang size of{' '}
              <span className="overhang-num" /> instead.
            </p>
          </div>
        </div>
        {showEnzymes && (
          <div className="form-group">
            <label className="col-md-3 control-label">Cut Site</label>
            <div className="col col-md-9">
              <div className="row">
                <div className="col col-md-4">
                  <select className="edit-primer-enzymes">
                    <option />
                    {enzymes.map((enzyme) => (
                      <option key={enzyme.id} value={enzyme.id}>
                        {enzyme.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col col-md-4">
                  <input type="text" readOnly={true} className="form-control js-reference" />
                </div>
              </div>
              <p className="help-block">Use the dropdown above to look up restriction sites.</p>
            </div>
          </div>
        )}
      </fieldset>
      <fieldset>
        <legend>
          Verify
          <div className="pull-right">
            <div className="secondary-temp">
              <button type="button" className="button button--small button--tertiaryGray js-check-secondary">
                Check Secondary Structure
              </button>{' '}
              at <input type="text" placeholder="37" />
              &deg;C
            </div>
          </div>
        </legend>
        <div className="form-group">
          <label className="col-md-3 control-label">
            T<sub>m</sub> <span className="settings glyphicon glyphicon-wrench js-tm-parameters u-pointer" />
          </label>
          <div className="col col-md-2 text-right js-tm input-field" />
        </div>
        <div className="form-group">
          <label className="col-md-3 control-label">GC Content</label>
          <div className="col col-md-2 text-right js-gc input-field" />
        </div>
        <div className="form-group">
          <label className="col-md-3 control-label">Length</label>
          <div className="col col-md-2 text-right js-length input-field" />
        </div>
        <div className="form-group js-secondary hidden">
          <label className="col-md-3 control-label">Min &Delta;G Homodimer</label>
          <div className="col col-md-2 text-right js-homodimer-dg input-field">
            <div className="spinner" />
          </div>
          <div className="col col-md-3 js-homodimer secondary-link invisible">
            <a>All Structures</a>
          </div>
        </div>
        <div className="form-group js-secondary hidden">
          <label className="col-md-3 control-label">Min &Delta;G Monomer</label>
          <div className="col col-md-2 text-right js-monomer-dg input-field">
            <div className="spinner" />
          </div>
          <div className="col col-md-3 js-monomer secondary-link invisible">
            <a>All Structures</a>
          </div>
        </div>
      </fieldset>
      <fieldset>
        <legend>Save</legend>
        <div className="form-group">
          <label className="col-md-3 control-label">Name</label>
          <div className="col col-md-8">
            <input type="text" name="primer-name" placeholder="Enter a short name" className="js-name" />
          </div>
          <div className="col col-md-1 text-right">
            <div className="swatch-big" />
          </div>
        </div>
        <div className="col col-md-9 col-md-offset-3 js-name-too-long invisible">
          <p className="text-warning error-msg">
            The primer name you have selected is too long. Primer names can be at most{' '}
            {MAX_PRIMER_NAME_LENGTH} characters long.
          </p>
        </div>
        <div className="form-group js-found-existing-name-group hidden">
          <div className="col col-md-9 col-md-offset-3 js-found-existing-name invisible">
            <p className="text-warning error-msg">
              A primer with name "<span className="existing-name" />" already exists in the designated
              library.
            </p>
          </div>
        </div>
        <div className="form-group save-library-group">
          <label className="col-md-3 control-label">Save To</label>
          <div className="col col-md-8 save-library">
            <div className="folder-selector-container" />
          </div>
        </div>
        <div className="form-group">
          <div className="col col-md-3 col-md-offset-8">
            <div className="pull-right">
              <button
                type="button"
                className="button button--primary button--small disabled js-save single-save"
                {...getTestProps({component: 'EditPrimerTab', element: 'savePrimerButton'})}
              >
                Save Primer
              </button>
            </div>
          </div>
        </div>
      </fieldset>
    </form>
  );
}

export default makeDeprecatedJadeStyleTemplate<EditPrimerProps>(EditPrimer);
